import React, { useEffect, useRef } from 'react'
import { Z_INDEX } from '@/constants/z-index'
import LoadingPercentage from '@/scenes/Editor/components/FloatingImprover/LoadingPercentage'

const GRADIENT_COLOR_STOPS =
  'rgba(244, 156, 192, 1), rgba(189, 255, 204, 1), rgba(186, 245, 255, 0.64), rgba(251, 133, 255, 1), rgba(220, 238, 121, 1), rgba(119, 177, 247, 1), rgba(244, 156, 192, 1), rgba(244, 156, 192, 1), rgba(189, 255, 204, 1)'

const GRADIENT_BACKGROUND_SIZE = '800% 800%'
const GRADIENT_DURATION = '4s'
const GRADIENT_BACKGROUND = `linear-gradient(-45deg, ${GRADIENT_COLOR_STOPS})`

export interface GradientProps {
  angle?: number
  top?: number
  left?: number
  width?: number
  height?: number
  fillContainer?: boolean
  borderRadius?: number
  style?: React.CSSProperties
  showPercentage?: boolean
  /** Expected operation duration (s) used to pace the percentage loader. */
  expectedDurationSeconds?: number
  clipPath?: {
    top: number
    left: number
    width: number
    height: number
    borderRadius?: number
  }
}

const GradientLoader: React.FC<GradientProps> = ({
  angle,
  top = 0,
  left = 0,
  width,
  height,
  fillContainer,
  borderRadius = 0,
  clipPath,
  style,
  showPercentage = false,
  expectedDurationSeconds,
}) => {
  const uniqueId = useRef(`gradient-loader-${Math.random().toString(36).substr(2, 9)}`)
  const componentId = uniqueId.current

  useEffect(() => {
    const styleId = `${componentId}-styles`
    if (!document.getElementById(styleId)) {
      const styleElement = document.createElement('style')
      styleElement.id = styleId
      const animationName = `${componentId}-animation`
      styleElement.innerHTML = `
        #${componentId} * { padding: 0; margin: 0; box-sizing: border-box; }
        @keyframes ${animationName} {
          0% { background-position: 1% 1%; }
          100% { background-position: 100% 100%; }
        }
        #${componentId} .gradient-wrap {
          position: absolute;
          opacity: 0.8;
          z-index: ${Z_INDEX.GRADIENT_LOADER};
          overflow: hidden;
        }
        #${componentId} .gradient {
          position: absolute;
          inset: -50%;
          background: ${GRADIENT_BACKGROUND};
          background-size: ${GRADIENT_BACKGROUND_SIZE};
          animation: ${animationName} ${GRADIENT_DURATION} linear infinite;
        }
      `
      document.head.appendChild(styleElement)
    }

    return () => {
      const styleElement = document.getElementById(styleId)
      if (styleElement && styleElement.parentNode) {
        styleElement.parentNode.removeChild(styleElement)
      }
    }
  }, [componentId])

  const containerStyle: React.CSSProperties = clipPath
    ? {
        width: `${clipPath.width}px`,
        height: `${clipPath.height}px`,
        position: 'absolute',
        top: `${clipPath.top}px`,
        left: `${clipPath.left}px`,
        overflow: 'hidden',
        borderRadius: `${clipPath.borderRadius ?? 16}px`,
        pointerEvents: 'none',
      }
    : {
        width: width != null ? `${width}px` : '100%',
        height: height != null ? `${height}px` : '100%',
        ...(showPercentage ? { position: 'relative' as const } : {}),
      };

  const useFill = fillContainer || width == null || height == null

  return (
    <div id={componentId} style={{ ...containerStyle, ...style }}>
      <div
        className="gradient-wrap"
        style={{
          width: useFill ? '100%' : `${width}px`,
          height: useFill ? '100%' : `${height}px`,
          transform: `rotate(${angle}deg)`,
          top: `${top}px`,
          left: `${left}px`,
          borderRadius: `${borderRadius}px`,
        }}
      >
        <div className="gradient" />
      </div>
      {showPercentage && (
        <LoadingPercentage
          expectedDurationSeconds={expectedDurationSeconds}
          offsetBottom={clipPath ? clipPath.height - top - (height ?? clipPath.height) : 0}
          offsetRight={clipPath ? clipPath.width - left - (width ?? clipPath.width) : 0}
        />
      )}
    </div>
  )
}

export default GradientLoader
